import { useEffect, useMemo, useState } from 'react';

const emptyState = { visibility: {}, ordering: [], pinning: { left: [], right: [] } };

export const reconcileResultsTableColumnOrder = (ordering = []) => {
  if (!Array.isArray(ordering) || ordering.length === 0 || ordering.includes('apiPresenceStatus')) return ordering;
  const lifecycleIndex = ordering.indexOf('enrollmentStatusCd');
  if (lifecycleIndex < 0) return ordering;
  return [
    ...ordering.slice(0, lifecycleIndex + 1),
    'apiPresenceStatus',
    ...ordering.slice(lifecycleIndex + 1),
  ];
};

const normalizeColumnState = (value) => {
  if (!value || typeof value !== 'object') return emptyState;
  if (!value.visibility && !value.ordering && !value.pinning) {
    return {
      visibility: value,
      ordering: [],
      pinning: { left: [], right: [] },
    };
  }
  return {
    visibility: value.visibility || {},
    ordering: reconcileResultsTableColumnOrder(Array.isArray(value.ordering) ? value.ordering : []),
    pinning:
      value.pinning && typeof value.pinning === 'object'
        ? {
            left: Array.isArray(value.pinning.left) ? value.pinning.left : [],
            right: Array.isArray(value.pinning.right) ? value.pinning.right : [],
          }
        : { left: [], right: [] },
  };
};

const loadColumnState = (storageKey) => {
  if (typeof window === 'undefined') return emptyState;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return emptyState;
    const parsed = JSON.parse(raw);
    return normalizeColumnState(parsed);
  } catch (_err) {
    return emptyState;
  }
};

const persistColumnState = (storageKey, state) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(state));
  } catch (_err) {
    // noop
  }
};

const useResultsTableColumnState = (storageKey) => {
  const initialState = useMemo(() => loadColumnState(storageKey), [storageKey]);
  const [columnVisibility, setColumnVisibility] = useState(initialState.visibility);
  const [columnOrder, setColumnOrder] = useState(initialState.ordering);
  const [columnPinning, setColumnPinning] = useState(initialState.pinning);

  useEffect(() => {
    persistColumnState(storageKey, {
      visibility: columnVisibility || {},
      ordering: columnOrder || [],
      pinning: columnPinning || { left: [], right: [] },
    });
  }, [storageKey, columnVisibility, columnOrder, columnPinning]);

  return {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    columnPinning,
    setColumnPinning,
    initialState,
  };
};

export default useResultsTableColumnState;
